import { type CSSProperties, useId } from 'react';

export interface AccordionLoaderProps {
	/** Width and height of the loader. Number = px, or any CSS length. Default 96. */
	size?: number | string;
	/** Seconds for one full expand + collapse. Default 1.6. */
	duration?: number;
	/** How far the outer layers travel, in logo units (logo is 320 wide). Default 34. */
	spread?: number;
	/** Accessible label read by screen readers. Default "Loading". */
	label?: string;
	className?: string;
	style?: CSSProperties;
}

const css = `
.accordion-loader { overflow: visible; display: inline-block; }
.accordion-loader .al-layer { transform-box: fill-box; transform-origin: center; }
.accordion-loader .al-top { animation: al-up var(--al-duration) ease-in-out infinite; }
.accordion-loader .al-bottom { animation: al-down var(--al-duration) ease-in-out infinite; }
@keyframes al-up { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(calc(var(--al-spread) * -1px)); } }
@keyframes al-down { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(calc(var(--al-spread) * 1px)); } }
@media (prefers-reduced-motion: reduce) {
  .accordion-loader .al-layer { animation-duration: calc(var(--al-duration) * 2); }
}
`;

export const AccordionLoader = ({
	size = 96,
	duration = 1.6,
	spread = 34,
	label = 'Loading',
	className,
	style,
}: AccordionLoaderProps) => {
	// Unique, URL-safe prefix so several loaders on one page don't share gradient IDs.
	const id = 'al' + useId().replace(/[^a-zA-Z0-9_-]/g, '');
	const left = `${id}-left`;
	const right = `${id}-right`;
	const darkA = `${id}-dark-a`;
	const darkB = `${id}-dark-b`;

	const vars = {
		'--al-duration': `${duration}s`,
		'--al-spread': spread,
		// Inline, not only in `css`: Bootstrap's reboot `svg:not(:root) { overflow: hidden }` outranks
		// `.accordion-loader`, which clipped the outer layers flat at the peak of every expand.
		'overflow': 'visible',
		'width': size,
		'height': size,
		...style,
	} as CSSProperties;

	return (
		<svg
			className={className ? `accordion-loader ${className}` : 'accordion-loader'}
			style={vars}
			viewBox='0 0 320 320'
			role='img'
			aria-label={label}
		>
			<style>{css}</style>
			<defs>
				<linearGradient
					id={left}
					x1='0'
					x2='1'
				>
					<stop
						offset='0'
						stopColor='#55BD7C'
					/>
					<stop
						offset='.55'
						stopColor='#4DB277'
					/>
					<stop
						offset='1'
						stopColor='#14614C'
					/>
				</linearGradient>
				<linearGradient
					id={right}
					x1='0'
					x2='1'
				>
					<stop
						offset='0'
						stopColor='#3EC095'
					/>
					<stop
						offset='.5'
						stopColor='#33AE88'
					/>
					<stop
						offset='1'
						stopColor='#0B5A45'
					/>
				</linearGradient>
				<linearGradient
					id={darkA}
					x1='0'
					x2='0'
					y1='0'
					y2='1'
				>
					<stop
						offset='0'
						stopColor='#0A4334'
					/>
					<stop
						offset='1'
						stopColor='#05221C'
					/>
				</linearGradient>
				<linearGradient
					id={darkB}
					x1='0'
					x2='0'
					y1='0'
					y2='1'
				>
					<stop
						offset='0'
						stopColor='#05221C'
					/>
					<stop
						offset='1'
						stopColor='#0A4334'
					/>
				</linearGradient>
			</defs>

			{/* Top layer: moves up */}
			<g className='al-layer al-top'>
				<polygon
					points='4,42 160,3 160,58 10,96 4,90'
					fill={`url(#${left})`}
				/>
				<polygon
					points='160,3 316,42 316,90 310,96 160,58'
					fill={`url(#${right})`}
				/>
				<polygon
					points='10,96 160,58 310,96 217,124 103,124'
					fill={`url(#${darkA})`}
				/>
			</g>

			{/* Middle layer: stays still */}
			<g className='al-layer al-middle'>
				<polygon
					points='4,133 160,120 160,201 4,187'
					fill={`url(#${left})`}
				/>
				<polygon
					points='160,120 316,133 316,187 160,201'
					fill={`url(#${right})`}
				/>
			</g>

			{/* Bottom layer: moves down */}
			<g className='al-layer al-bottom'>
				<polygon
					points='103,196 217,196 310,224 160,262 10,224'
					fill={`url(#${darkB})`}
				/>
				<polygon
					points='4,229 160,262 160,317 4,278'
					fill={`url(#${left})`}
				/>
				<polygon
					points='160,262 316,229 316,278 160,317'
					fill={`url(#${right})`}
				/>
			</g>
		</svg>
	);
};

export default AccordionLoader;
